<!-- eslint-disable vue/no-root-v-if -->
<script setup>
// NOTICE: KAPTION-INTEGRATION
// Live translation preview for reply box - shows translation as agent types
import { ref, watch, computed, onMounted, onUnmounted, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import ConversationApi from 'dashboard/api/inbox/conversation';
import Spinner from 'shared/components/Spinner.vue';
import { emitter } from 'shared/helpers/mitt';
import { useKaptionAIFeaturesPreferences } from 'dashboard/composables/useKaptionAIFeaturesPreferences';
import { useUISettings } from 'dashboard/composables/useUISettings';
// NOTICE: KAPTION-INTEGRATION - usage-analytics.TRANSLATION.2
import { trackUsage } from 'dashboard/composables/useUsageAnalytics';
import { USAGE_ANALYTICS_EVENTS } from 'dashboard/helper/usageAnalytics/constants';
import KaptionCircleFlag from 'dashboard/components-next/flag/KaptionCircleFlag.vue';

const props = defineProps({
  conversationId: {
    type: Number,
    required: true,
  },
  replyText: {
    type: String,
    default: '',
  },
  sourceLanguage: {
    type: String,
    default: 'en',
  },
  targetLanguage: {
    type: String,
    default: 'es',
  },
  enabled: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const translatedText = ref('');
const isTranslating = ref(false);
const translationError = ref(false);
const isTranslationReady = ref(false);
// NOTICE: KAPTION-INTEGRATION - contextual-translation.LIVE_REPLY.2
// Capture the engine label + context_aware the backend reports so the reply box
// persists the truthful provider (e.g. kaption_hosted_gemma / context_aware:true)
// instead of hard-coding "kaption_live_reply / context_aware:false".
const translationProvider = ref(null);
const translationContextAware = ref(false);

// NOTICE: KAPTION-INTEGRATION - Track scroll geometry on the inner text wrapper
// so we can fade in/out top/bottom gradients and a chevron-down hint when the
// translation overflows the cap. Mirrors the pattern in
// KaptionChatSuggestions.vue so both AI panels behave consistently.
const scrollContainer = ref(null);
const scrollTop = ref(0);
const scrollHeight = ref(0);
const clientHeight = ref(0);

const showTopGradient = computed(
  () => scrollHeight.value > clientHeight.value && scrollTop.value > 5
);
const showBottomGradient = computed(
  () =>
    scrollHeight.value > clientHeight.value &&
    scrollTop.value < scrollHeight.value - clientHeight.value - 5
);
const showScrollIndicator = computed(
  () => scrollHeight.value > clientHeight.value && scrollTop.value < 10
);

const onScroll = event => {
  const target = event.target;
  scrollTop.value = target.scrollTop;
  scrollHeight.value = target.scrollHeight;
  clientHeight.value = target.clientHeight;
};

const updateScrollState = () => {
  // Defer until after the DOM has the new translation rendered, so
  // scrollHeight reflects the final content length.
  requestAnimationFrame(() => {
    const el = scrollContainer.value;
    if (!el) return;
    scrollTop.value = el.scrollTop;
    scrollHeight.value = el.scrollHeight;
    clientHeight.value = el.clientHeight;
  });
};

// Re-measure whenever the translated text changes — content swap can flip a
// short translation into one that overflows the cap, and vice versa.
watch(translatedText, () => updateScrollState());

// NOTICE: KAPTION-INTEGRATION - Track if translation is needed but not yet available
// This is used to lock the send button while waiting for translation
const isWaitingForTranslation = ref(false);

// NOTICE: KAPTION-INTEGRATION - Track the last translation timestamp for maxWait enforcement
// eslint-disable-next-line no-unused-vars
let lastTranslationTime = 0;
let forceTranslationTimeout = null;

// NOTICE: KAPTION-INTEGRATION - Use the same composable as KaptionAIFeaturesPill
// This ensures preferences are synced across all components
const conversationIdRef = toRef(props, 'conversationId');
const { translationEnabled, setTranslation } =
  useKaptionAIFeaturesPreferences(conversationIdRef);

// NOTICE: KAPTION-INTEGRATION - Get master toggle from uiSettings (topbar setting)
const { uiSettings } = useUISettings();
const masterToggleEnabled = computed(
  () => uiSettings.value?.kaption_live_translation_enabled ?? true
);

// Check if source and target languages are the same
const isSameLanguage = computed(() => {
  return props.sourceLanguage === props.targetLanguage;
});

// Should show translation UI
// NOTICE: KAPTION-INTEGRATION - Check both master toggle AND per-conversation preference
const shouldShowTranslation = computed(() => {
  return (
    props.enabled &&
    masterToggleEnabled.value &&
    translationEnabled.value &&
    !isSameLanguage.value
  );
});

// NOTICE: KAPTION-INTEGRATION - targetLanguageEmoji computed removed, using KaptionCircleFlag component instead

// NOTICE: KAPTION-INTEGRATION - usage-analytics.TRANSLATION.2
// Emits live_translation_performed for both success and failure. Metadata-only:
// language pair + direction + latency + provider/context_aware. Best-effort.
const trackTranslationPerformed = (succeeded, startedAt, extra = {}) => {
  try {
    trackUsage(USAGE_ANALYTICS_EVENTS.LIVE_TRANSLATION_PERFORMED, {
      succeeded: Boolean(succeeded),
      language_pair: `${props.sourceLanguage}->${props.targetLanguage}`,
      direction: 'outgoing', // reply-box preview: agent -> customer
      latency_ms: Date.now() - startedAt,
      provider: extra.provider ?? null,
      context_aware: Boolean(extra.context_aware),
    });
  } catch (e) {
    // best-effort
  }
};

// NOTICE: KAPTION-INTEGRATION - Actual translation function
const performTranslation = async text => {
  // Don't translate very short text
  if (!text || text.trim().length < 3) {
    translatedText.value = '';
    isTranslationReady.value = false;
    isWaitingForTranslation.value = false;
    return;
  }

  // Mark that we're waiting for translation (blocks send button)
  isWaitingForTranslation.value = true;
  isTranslating.value = true;
  isTranslationReady.value = false;
  translationError.value = false;
  lastTranslationTime = Date.now();
  // NOTICE: KAPTION-INTEGRATION - usage-analytics.TRANSLATION.2 (latency)
  const startedAt = Date.now();

  try {
    const response = await ConversationApi.translateLive({
      conversationId: props.conversationId,
      text: text.trim(),
      sourceLanguage: props.sourceLanguage,
      targetLanguage: props.targetLanguage,
    });

    const translation = response.data.translation;
    translatedText.value = translation.text;
    // NOTICE: KAPTION-INTEGRATION - contextual-translation.LIVE_REPLY.2
    translationProvider.value = translation.provider ?? null;
    translationContextAware.value = translation.context_aware ?? false;
    isTranslationReady.value = true;
    isWaitingForTranslation.value = false; // Translation ready, unblock send
    // NOTICE: KAPTION-INTEGRATION - usage-analytics.TRANSLATION.2
    // provider + context_aware come straight from the API response (never
    // hard-coded). No text is emitted — only the language pair + latency.
    trackTranslationPerformed(true, startedAt, {
      provider: translation.provider ?? null,
      context_aware: translation.context_aware ?? false,
    });
  } catch (error) {
    translationError.value = true;
    translatedText.value = '';
    translationProvider.value = null;
    translationContextAware.value = false;
    isTranslationReady.value = false;
    isWaitingForTranslation.value = false; // Error occurred, allow sending in original language
    // NOTICE: KAPTION-INTEGRATION - usage-analytics.TRANSLATION.2 (failure path)
    trackTranslationPerformed(false, startedAt, {
      provider: null,
      context_aware: false,
    });
  } finally {
    isTranslating.value = false;
  }
};

// NOTICE: KAPTION-INTEGRATION - Debounced translation with enforced maxWait
// This ensures translation happens even if user keeps typing
let pendingTranslationText = null;
const MAX_WAIT_TIME = 3500; // 3.5 seconds max wait (DeepSeek ~2000ms avg + buffer)
const DEBOUNCE_TIME = 800; // 800ms debounce (batch keystrokes before hitting DeepSeek)

const translateText = debounce(
  async text => {
    // Clear the force translation timeout if it exists
    if (forceTranslationTimeout) {
      clearTimeout(forceTranslationTimeout);
      forceTranslationTimeout = null;
    }

    // Perform translation immediately
    await performTranslation(text);
  },
  DEBOUNCE_TIME,
  { maxWait: MAX_WAIT_TIME }
);

// Watch for changes in reply text
watch(
  () => props.replyText,
  newText => {
    if (shouldShowTranslation.value) {
      // Store the pending text for potential force translation
      pendingTranslationText = newText;

      // Call debounced translation
      translateText(newText);

      // NOTICE: KAPTION-INTEGRATION - Enforce maxWait by forcing translation after MAX_WAIT_TIME
      // This ensures translation happens even if user keeps typing continuously
      if (!forceTranslationTimeout) {
        forceTranslationTimeout = setTimeout(() => {
          if (
            pendingTranslationText &&
            pendingTranslationText.trim().length >= 3
          ) {
            // Force translation of the last pending text
            performTranslation(pendingTranslationText);
          }
          forceTranslationTimeout = null;
        }, MAX_WAIT_TIME);
      }
    } else {
      translatedText.value = '';
      if (forceTranslationTimeout) {
        clearTimeout(forceTranslationTimeout);
        forceTranslationTimeout = null;
      }
    }
  }
);

// Watch for language changes and clear translation
watch(
  () => [props.sourceLanguage, props.targetLanguage],
  () => {
    translatedText.value = '';
    isTranslationReady.value = false;
  }
);

// NOTICE: KAPTION-INTEGRATION - Watch translationEnabled to clear translation when disabled
watch(
  () => translationEnabled.value,
  enabled => {
    if (!enabled) {
      translatedText.value = '';
      isTranslationReady.value = false;
    }
  }
);

// NOTICE: KAPTION-INTEGRATION - Listen for translation toggle events
// Update the composable's ref when the event fires to maintain reactivity
const handleTranslationToggle = ({ conversationId, enabled }) => {
  if (conversationId === props.conversationId) {
    // Update the composable's ref to maintain reactivity
    setTranslation(enabled);
    // Clear translation when disabled
    if (!enabled) {
      translatedText.value = '';
      isTranslationReady.value = false;
    }
  }
};

onMounted(() => {
  emitter.on('kaption-translation-toggled', handleTranslationToggle);
});

onUnmounted(() => {
  emitter.off('kaption-translation-toggled', handleTranslationToggle);

  // NOTICE: KAPTION-INTEGRATION - Clean up timeout on unmount
  if (forceTranslationTimeout) {
    clearTimeout(forceTranslationTimeout);
    forceTranslationTimeout = null;
  }
});

// Expose values for parent component (including translation ready state and waiting flag)
defineExpose({
  translatedText,
  targetLanguage: computed(() => props.targetLanguage),
  isTranslationReady,
  isTranslating,
  isWaitingForTranslation, // NOTICE: KAPTION-INTEGRATION - Expose for send button locking
  // NOTICE: KAPTION-INTEGRATION - contextual-translation.LIVE_REPLY.2
  // Expose the real engine label so ReplyBox persists provider/context_aware truthfully.
  translationProvider,
  translationContextAware,
});
</script>

<template>
  <!-- NOTICE: KAPTION-INTEGRATION - Outer height capped (matches the AI
       suggestion card pattern in KaptionChatSuggestions.vue) so a long
       translation can't cover the conversation thread on small / zoomed
       laptop screens (BFG/Jony, 2026-04-30 video). The inner text area
       owns the scroll instead of growing the box. -->
  <div
    v-if="shouldShowTranslation"
    class="mb-2 px-3 py-2 bg-n-green-3 rounded-2xl relative max-h-56 overflow-hidden"
  >
    <!-- Floating spinner in top right -->
    <div v-if="isTranslating" class="absolute top-2 right-2">
      <Spinner size="small" />
    </div>

    <div class="flex items-start gap-2">
      <KaptionCircleFlag
        :language-code="targetLanguage"
        size="size-5"
        class="flex-shrink-0 mt-0.5"
      />
      <!-- NOTICE: KAPTION-INTEGRATION - Inner scroll container so multi-line
           translations (e.g. country lists, multi-paragraph replies) stay
           confined; mirrors the max-h-32 inner scroll on the AI suggestion
           card. pr-1 leaves room for the scrollbar without clipping text.
           `relative` positions the gradient/chevron overlays, and the
           gradients themselves are sticky inside this scroller so they
           stay anchored as the user scrolls. -->
      <div
        ref="scrollContainer"
        class="max-h-44 overflow-y-auto pr-1 min-w-0 flex-1 relative"
        @scroll="onScroll"
      >
        <!-- Top gradient — appears once scrolled away from the top.
             Color matches the translation card's bg-n-green-3/30 so the
             fade dissolves cleanly into the parent panel. -->
        <div
          v-if="showTopGradient"
          class="sticky top-0 left-0 right-0 h-8 pointer-events-none transition-opacity duration-200 bg-gradient-to-b from-n-green-3 to-n-green-3/0 z-10"
        />

        <span
          v-if="translatedText"
          class="text-sm text-n-slate-11 leading-relaxed whitespace-pre-wrap break-words"
        >
          {{ translatedText }}
        </span>
        <span
          v-else-if="!isTranslating && !translationError"
          class="text-sm text-n-slate-11 leading-relaxed italic"
        >
          {{ t('CONVERSATION.LIVE_TRANSLATION.START_TYPING') }}
        </span>
        <span v-else-if="translationError" class="text-xs text-n-red-11">
          {{ t('CONVERSATION.LIVE_TRANSLATION.UNAVAILABLE') }}
        </span>

        <!-- Bottom gradient — appears when there's more content below the cap. -->
        <div
          v-if="showBottomGradient"
          class="sticky bottom-0 left-0 right-0 h-8 pointer-events-none transition-opacity duration-200 bg-gradient-to-t from-n-green-3 to-n-green-3/0 z-10"
        />

        <!-- Bouncing chevron-down hint — only while at the top with more
             content waiting; mirrors KaptionChatSuggestions' indicator. -->
        <Transition name="fade">
          <div
            v-if="showScrollIndicator"
            class="absolute bottom-1 right-2 pointer-events-none z-10"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="2.5"
              stroke="currentColor"
              class="w-3.5 h-3.5 text-n-green-9 animate-bounce"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3"
              />
            </svg>
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>
